import api from '../axios.config';
import { ApiResponse } from 'types';
import { apiError } from '../apiError';
import { responseFrom } from '../apiEnvelope';

/**
 * Stub endpoints — a URL that answers with exactly what you told it to, and a
 * log of who called it.
 *
 * Backend: core-service `/v1/mock-endpoints` for the owner's side, and
 * `/v1/mock/{token}` for the URL itself, which answers without a login.
 */
const BASE = '/v1/mock-endpoints';
const err = (e: any, msg: string) => apiError(e, msg);

export interface MockEndpoint {
  id?: number;
  name?: string;
  description?: string | null;
  /** The unguessable half of the URL. Shown so it can be rotated, never edited. */
  token?: string;
  /**
   * The full URL to hand somebody.
   *
   * NULL when the deployment has not been told its own public address
   * (`cpass.public-base-url`) — the screen says so rather than showing a link
   * that answers nothing.
   */
  url?: string | null;
  status_code?: number;
  content_type?: string;
  body?: string | null;
  delay_ms?: number;
  enabled?: boolean;
  capture_requests?: boolean;
  expires_at?: string | null;
  expired?: boolean;
  /** Enabled AND not expired — what the screen should show as a state. */
  live?: boolean;
  hit_count?: number;
  last_hit_at?: string | null;
  created_at?: string;
  updated_at?: string;
  created_by?: string | null;
}

/** One inbound hit. Everything here was written by a stranger — render as text. */
export interface MockEndpointHit {
  id: number;
  endpoint_id: number;
  method: string;
  query_string?: string | null;
  /** JSON object as a string; credential-bearing headers arrive as "[redacted]". */
  headers?: string | null;
  content_type?: string | null;
  body?: string | null;
  body_truncated?: boolean;
  body_bytes?: number;
  remote_ip?: string | null;
  received_at?: string | null;
}

/** What the backend will accept, so the form states the limits up front. */
export interface MockEndpointLimits {
  allowed_content_types: string[];
  max_endpoints_per_org: number;
  max_body_bytes: number;
  max_delay_ms: number;
  rate_limit_per_minute: number;
  keep_requests_per_endpoint: number;
  max_captured_body_bytes: number;
}

export interface MockEndpointHits {
  content: MockEndpointHit[];
  page: number;
  size: number;
  total: number;
  last: boolean;
}

/**
 * One time window on a stub, and the response it selects.
 *
 * A stub can answer differently by hour: the one this was built for tells a
 * telephony vendor which team takes the call, which is ~20 people during the
 * day and 2 overnight.
 */
export interface MockEndpointWindow {
  id?: number;
  /** Shown on the row and logged on every hit, so a surprising route is traceable. */
  name?: string;
  /** Lower is checked first. The first OPEN window wins. */
  priority?: number;
  /** 'HH:MM' — inclusive. */
  start_time?: string;
  /** 'HH:MM' — EXCLUSIVE, and earlier than start means the window runs through midnight. */
  end_time?: string;
  /** 'mon,tue,wed' or empty for every day. */
  days?: string | null;
  /** IANA zone. Windows are judged here, not in the server's UTC. */
  zone_id?: string;
  status_code?: number | null;
  content_type?: string | null;
  body?: string | null;
  enabled?: boolean;
  /** Server-computed: start later than end, i.e. an overnight window. */
  crosses_midnight?: boolean;
}

/** Which window is answering right now. */
export interface MockEndpointWindowStatus {
  open_window: string | null;
  window_count: number;
  /** 'window' when a schedule is answering, 'default' when the stub's own body is. */
  serving: 'window' | 'default';
}

export const mockEndpointsService = {
  list: async (): Promise<ApiResponse<MockEndpoint[]>> => {
    try { return responseFrom((await api.get(BASE)).data); }
    catch (e: any) { return err(e, 'Failed to load stub endpoints'); }
  },

  limits: async (): Promise<ApiResponse<MockEndpointLimits>> => {
    try { return responseFrom((await api.get(`${BASE}/limits`)).data); }
    catch (e: any) { return err(e, 'Failed to load the limits'); }
  },

  get: async (id: number): Promise<ApiResponse<MockEndpoint>> => {
    try { return responseFrom((await api.get(`${BASE}/${id}`)).data); }
    catch (e: any) { return err(e, 'Failed to load that stub endpoint'); }
  },

  create: async (body: Partial<MockEndpoint>): Promise<ApiResponse<MockEndpoint>> => {
    try { return responseFrom((await api.post(BASE, body)).data); }
    catch (e: any) { return err(e, 'Failed to create the stub endpoint'); }
  },

  update: async (id: number, body: Partial<MockEndpoint>): Promise<ApiResponse<MockEndpoint>> => {
    try { return responseFrom((await api.put(`${BASE}/${id}`, body)).data); }
    catch (e: any) { return err(e, 'Failed to save the stub endpoint'); }
  },

  /** A new URL for the same stub. Configuration and history are kept. */
  rotateToken: async (id: number): Promise<ApiResponse<MockEndpoint>> => {
    try { return responseFrom((await api.post(`${BASE}/${id}/rotate-token`, {})).data); }
    catch (e: any) { return err(e, 'Failed to issue a new URL'); }
  },

  remove: async (id: number): Promise<ApiResponse<void>> => {
    try { return responseFrom((await api.delete(`${BASE}/${id}`)).data); }
    catch (e: any) { return err(e, 'Failed to delete the stub endpoint'); }
  },

  hits: async (id: number, page = 0, size = 25): Promise<ApiResponse<MockEndpointHits>> => {
    try { return responseFrom((await api.get(`${BASE}/${id}/requests`, { params: { page, size } })).data); }
    catch (e: any) { return err(e, 'Failed to load what callers sent'); }
  },

  clearHits: async (id: number): Promise<ApiResponse<void>> => {
    try { return responseFrom((await api.delete(`${BASE}/${id}/requests`)).data); }
    catch (e: any) { return err(e, 'Failed to clear the log'); }
  },

  // ── time windows ──────────────────────────────────────────────────────────

  windows: async (id: number): Promise<ApiResponse<MockEndpointWindow[]>> => {
    try { return responseFrom((await api.get(`${BASE}/${id}/schedules`)).data); }
    catch (e: any) { return err(e, 'Failed to load the time windows'); }
  },

  /** What is answering right now — checkable without waiting for a real call. */
  windowStatus: async (id: number): Promise<ApiResponse<MockEndpointWindowStatus>> => {
    try { return responseFrom((await api.get(`${BASE}/${id}/schedules/status`)).data); }
    catch (e: any) { return err(e, 'Failed to check which window is open'); }
  },

  createWindow: async (id: number, body: Partial<MockEndpointWindow>): Promise<ApiResponse<MockEndpointWindow>> => {
    try { return responseFrom((await api.post(`${BASE}/${id}/schedules`, body)).data); }
    catch (e: any) { return err(e, 'Failed to add the time window'); }
  },

  updateWindow: async (id: number, windowId: number, body: Partial<MockEndpointWindow>): Promise<ApiResponse<MockEndpointWindow>> => {
    try { return responseFrom((await api.put(`${BASE}/${id}/schedules/${windowId}`, body)).data); }
    catch (e: any) { return err(e, 'Failed to save the time window'); }
  },

  removeWindow: async (id: number, windowId: number): Promise<ApiResponse<void>> => {
    try { return responseFrom((await api.delete(`${BASE}/${id}/schedules/${windowId}`)).data); }
    catch (e: any) { return err(e, 'Failed to delete the time window'); }
  },
};

export default mockEndpointsService;
